/**
 * Caller IDs a user may pick for an outbound call (KAN-178).
 *
 * Two Teams & Permissions settings hide a number for a shared user:
 *   1. Calls → "Caller ID Visibility" (`calls.global.hidden_caller_ids`)
 *   2. Senders/Numbers → level `hidden`
 *
 * The SMS sender picker already honours (2) via useSenders; the dialers read
 * the raw `callerIds` and only checked (1) — and only on the _default group,
 * never the user's own team. Every dialer dropdown reads this hook instead so
 * calls and SMS agree. Admins see everything.
 */

import { useEffect, useMemo } from 'react';
import { useCallStore } from '@/stores/call-store';
import { useSettingsStore } from '@/stores/settings-store';
import { usePermissions, senderPermissionHidesSender } from './use-permissions';
import type { CallerID } from '@/types/voice';

/**
 * The saved default caller ID (dialer ⭐) last applied to the selection.
 * Module-level so every dialer surface shares it: the default is applied once
 * per value, and a manual pick afterwards is never overridden by another mount.
 */
let appliedDefaultCallerId: string | undefined;

/**
 * The id the Senders/Numbers permission list uses for a caller ID. Twilio keys
 * SMS senders by phone-number SID and WhatsApp senders by `wa_<sender SID>`
 * (twilio.module getSenders); _buildCallerIds carries the same SIDs as `id`.
 */
export function senderIdForCallerId(cid: CallerID): string {
  return cid.channel === 'WHATSAPP' ? `wa_${cid.id}` : cid.id;
}

export function useVisibleCallerIds(): CallerID[] {
  const callerIds = useCallStore((s) => s.callerIds);
  const selectedCallerId = useCallStore((s) => s.selectedCallerId);
  const { isAdmin, permissionsReady, callPermissions, getSenderPermission } = usePermissions();
  const hiddenCallerIds = callPermissions.hiddenCallerIds;

  const visible = useMemo(() => {
    if (isAdmin) return callerIds;
    return callerIds.filter(
      (c) =>
        !hiddenCallerIds.includes(c.value) &&
        !senderPermissionHidesSender(getSenderPermission(senderIdForCallerId(c)))
    );
  }, [isAdmin, callerIds, hiddenCallerIds, getSenderPermission]);

  // The store seeds selectedCallerId with callerIds[0], which may be a number
  // this user must not see. Move the selection to the first visible one.
  useEffect(() => {
    if (!permissionsReady || !selectedCallerId) return;
    if (!callerIds.some((c) => c.value === selectedCallerId)) return;
    if (visible.some((c) => c.value === selectedCallerId)) return;
    useCallStore.setState({ selectedCallerId: visible[0]?.value ?? null });
  }, [permissionsReady, selectedCallerId, callerIds, visible]);

  // The store also seeds selectedCallerId before preferences are read, so the
  // saved default never won after a reload. Apply it once caller IDs load.
  const defaultCallerId = useSettingsStore((s) => s.preferences.defaultCallerId);
  const settingsLoaded = useSettingsStore((s) => s.settingsLoaded);
  useEffect(() => {
    if (!permissionsReady || !settingsLoaded || !defaultCallerId) return;
    if (defaultCallerId === appliedDefaultCallerId) return;
    if (!visible.some((c) => c.value === defaultCallerId)) return;
    appliedDefaultCallerId = defaultCallerId;
    useCallStore.setState({ selectedCallerId: defaultCallerId });
  }, [permissionsReady, settingsLoaded, defaultCallerId, visible]);

  return visible;
}
